import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowRight,
  ArrowUpRight,
  ChevronRight,
  Eye,
  EyeOff,
  LockKeyhole,
  MoreHorizontal,
  Plus,
  Send,
  ShieldCheck,
  Sparkles,
  WalletCards,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { ErrorState } from "@/components/ErrorState";
import Loader from "@/components/loader/Loader";

import { useMyNotifications, useMyWalletAndAssets, useProfile } from "@/hooks";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import type { WalletAsset } from "@/interfaces/asset";
import type {
  FormattedTransaction,
  FormattedTransactionInterface,
} from "@/interfaces/transaction";

import { useQuery } from "@tanstack/react-query";
import { makeRequest } from "@/utils/makeRequest";

import AssetItem from "@/components/shared/asset/AssetItem";
import { getGreeting } from "@/utils/get-greeting";
import { toggleBalance } from "@/store/slices/settingsSlice";
import { formatCurrency } from "@/utils/formatCurrency";
import { TransactionRow } from "@/components/user/TransactionRow";
import { copyToClipboard } from "@/utils";
import TransactionReceipt from "@/components/user/TransactionReceipt";

type DashboardMode = "overview" | "deposit" | "swap";

export default function UserHomePage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showBalance } = useAppSelector((state) => state.setting);

  const { user } = useAppSelector((state) => state.auth);
  const userId = user?.id ?? "";

  const [activeMode, setActiveMode] = useState<DashboardMode>("overview");

  const [selectedAsset, setSelectedAsset] = useState<WalletAsset>();
  const [selectedTx, setSelectedTx] = useState<FormattedTransaction | null>(
    null,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    data: profile,
    isLoading: isLoadingProfile,
    isError: isProfileError,
    refetch: refetchProfile,
  } = useProfile(userId);

  const {
    data: wallet,
    isLoading: isLoadingWallet,
    isError: isWalletError,
    refetch: refetchWallet,
  } = useMyWalletAndAssets(userId);

  const { data: notifications = [], isLoading: isLoadingNotifications } =
    useMyNotifications(userId);

  const fetchRecentTransactions = async () => {
    const response = await makeRequest.get("/transaction/me", {
      params: {
        page: 1,
        limit: 5,
      },
    });

    return response.data;
  };

  const { data: transactionData, isLoading: isLoadingTransactions } =
    useQuery<FormattedTransactionInterface>({
      queryKey: ["recent-transactions", userId],
      queryFn: fetchRecentTransactions,
      enabled: Boolean(userId),
    });

  const assets: WalletAsset[] = wallet?.assets ?? [];

  const [page, setPage] = useState(1);

  const fetchTransactions = async () => {
    const res = await makeRequest.get("/transaction/me", {
      params: { page, limit: 10 },
    });
    return res.data;
  };

  const {
    data: transactions,
    isLoading,
    isError,
    refetch,
  } = useQuery<FormattedTransactionInterface>({
    queryKey: ["transactions", user?.id, page],
    queryFn: fetchTransactions,
    enabled: !!user?.id,
  });

  const openReceipt = (tx: FormattedTransaction) => {
    setSelectedTx(tx);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (assets.length > 0 && !selectedAsset) {
      setSelectedAsset(assets[0]);
    }
  }, [assets, selectedAsset]);

  const totalPortfolioValue = useMemo(() => {
    return assets.reduce(
      (total, asset) => total + Number(asset.convertedBalance ?? 0),
      0,
    );
  }, [assets]);

  const sortedAssets = useMemo(() => {
    return [...assets].sort((a, b) => {
      return Number(b.convertedBalance ?? 0) - Number(a.convertedBalance ?? 0);
    });
  }, [assets]);

  const topAssets = sortedAssets.slice(0, 5);

  if (
    isLoadingProfile ||
    isLoadingWallet ||
    isLoadingNotifications ||
    isLoadingTransactions
  ) {
    return <Loader type="bounce" />;
  }

  if (isProfileError || isWalletError) {
    return (
      <ErrorState
        onRetry={() => {
          refetchProfile();
          refetchWallet();
        }}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1600px] space-y-6 pb-10">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">
              Financial overview
            </p>

            <span className="h-1 w-1 rounded-full bg-emerald-500" />
            <span className="text-[11px] text-muted-foreground">
              Account active
            </span>
          </div>

          <h1 className="mt-1 text-2xl font-bold tracking-[-0.035em] sm:text-3xl">
            {getGreeting()}, {profile?.firstName || "there"}
          </h1>
        </div>
      </header>

      {/* ECO Presale Notice */}
      <div className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <Sparkles className="h-5 w-5 shrink-0 text-primary" />

          <p className="text-sm text-foreground">
            <span className="font-semibold">ECO Presale is Live</span>
            <span className="hidden sm:inline text-muted-foreground">
              {" "}
              — Buy ECO at the current presale price.
            </span>
          </p>
        </div>

        <a
          href="https://cowrie-vip-presale-mobile-walletcon.vercel.app/"
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 text-sm font-semibold text-primary hover:underline"
        >
          View & Buy ECO →
        </a>
      </div>

      {/* ========================================================
          PORTFOLIO HERO
      ======================================================== */}
      <section className="relative overflow-hidden rounded-[1.75rem] border border-border/60 bg-card">
        <div className="pointer-events-none absolute -right-24 -top-32 size-[420px] rounded-full bg-primary/[0.09] blur-[100px]" />

        <div className="relative p-5 sm:p-7">
          <div className="w-full">
            {/* HEADER */}
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <WalletCards className="size-4 text-primary" />
                Total portfolio
              </div>

              <button
                type="button"
                onClick={() => dispatch(toggleBalance())}
                aria-label={showBalance ? "Hide balance" : "Show balance"}
                className="flex size-9 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.055] text-white/55 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] backdrop-blur-md transition-all duration-200 hover:border-primary/25 hover:bg-white/[0.1] hover:text-white"
              >
                {showBalance ? (
                  <Eye className="size-4" />
                ) : (
                  <EyeOff className="size-4" />
                )}
              </button>
            </div>

            {/* CONTENT */}
            <div className="mt-4 flex w-full flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
              {/* BALANCE */}
              <div>
                <div className="flex items-center">
                  <h2 className="text-4xl font-black tracking-[-0.05em] sm:text-5xl">
                    {wallet?.totalBalanceCurrency}{" "}
                    {showBalance
                      ? formatCurrency(totalPortfolioValue)
                      : "••••••"}
                  </h2>
                </div>

                <p className="mt-2 text-xs text-muted-foreground">
                  Estimated value across all your assets
                </p>
              </div>

              {/* ACTIONS */}
              <div className="flex flex-wrap gap-2 lg:justify-end">
                <Button
                  className="rounded-xl"
                  onClick={() => navigate("/dashboard/deposit")}
                >
                  <Plus className="mr-2 size-4" />
                  Deposit
                </Button>

                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => navigate("/dashboard/transfer")}
                >
                  <Send className="mr-2 size-4" />
                  Send
                </Button>

                <Button
                  variant="outline"
                  className="rounded-xl"
                  onClick={() => navigate("/dashboard/swap")}
                >
                  <ArrowLeftRight className="mr-2 size-4" />
                  Swap
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================
          ACTION WORKSPACE
      ======================================================== */}

      {activeMode !== "overview" && (
        <section className="overflow-hidden rounded-[1.75rem] border border-border/60 bg-card">
          <div className="border-b border-border/60 px-5 py-4 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-bold">
                  {activeMode === "deposit" ? "Deposit funds" : "Swap assets"}
                </p>

                <p className="mt-0.5 text-xs text-muted-foreground">
                  Complete your transaction securely.
                </p>
              </div>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setActiveMode("overview")}
              >
                Close
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* ========================================================
          MAIN GRID
      ======================================================== */}

      {activeMode === "overview" && (
        <div className="grid gap-6 lg:grid-cols-[1.45fr_0.75fr]">
          {/* LEFT */}

          <div className="space-y-6">
            {/* Quick actions */}

            <section className="rounded-[1.5rem] border border-border/60 bg-card p-5 sm:p-6">
              <div className="mb-5">
                <p className="text-sm font-bold">Quick actions</p>

                <p className="mt-1 text-xs text-muted-foreground">
                  Move money without navigating through menus.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <QuickAction
                  icon={ArrowDownLeft}
                  title="Deposit"
                  description="Add funds"
                  onClick={() => navigate("/dashboard/deposit")}
                />

                <QuickAction
                  icon={ArrowUpRight}
                  title="Withdraw"
                  description="Cash out"
                  onClick={() => navigate("/dashboard/withdraw")}
                />

                <QuickAction
                  icon={Send}
                  title="Send"
                  description="Transfer assets"
                  onClick={() => navigate("/dashboard/transfer")}
                />

                <QuickAction
                  icon={ArrowLeftRight}
                  title="Swap"
                  description="Exchange assets"
                  onClick={() => navigate("/dashboard/swap")}
                />
              </div>
            </section>

            {/* Assets */}

            <section className="rounded-[1.5rem] border border-border/60 bg-card">
              <DashboardSectionHeader
                title="Your assets"
                description="Current balances across your portfolio."
                action="View all"
                onClick={() => navigate("/dashboard/assets")}
              />

              <div className="space-y-2">
                {topAssets.map((asset) => (
                  <AssetItem key={asset.id} asset={asset} />
                ))}
              </div>
            </section>

            {/* Recent activity */}

            <section className="rounded-[1.5rem] border border-border/60 bg-card">
              <DashboardSectionHeader
                title="Recent activity"
                description="Your latest account activity."
                action="View all"
                onClick={() => navigate("/dashboard/transactions")}
              />

              <div className="divide-y">
                {transactions?.data?.slice(0, 5).map((tx) => (
                  <TransactionRow
                    key={tx.id}
                    tx={tx}
                    onClick={() => openReceipt(tx)}
                    onCopy={(e) => {
                      e.stopPropagation();
                      copyToClipboard(tx.id);
                    }}
                  />
                ))}
              </div>
            </section>
          </div>

          {/* RIGHT */}
          <div className="space-y-6">
            {/* Portfolio composition */}

            <section className="rounded-[1.5rem] border border-border/60 bg-card p-5 sm:p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-bold">Portfolio</p>

                  <p className="mt-1 text-xs text-muted-foreground">
                    Asset distribution
                  </p>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  className="size-8 rounded-lg"
                >
                  <MoreHorizontal className="size-4" />
                </Button>
              </div>

              <div className="mt-6 flex justify-center">
                <div className="relative flex size-44 items-center justify-center rounded-full border-[18px] border-primary/20">
                  <div className="absolute inset-0 rounded-full border-[18px] border-transparent border-t-primary border-r-primary rotate-45" />

                  <div className="text-center">
                    <p className="text-2xl font-black">{assets.length}</p>

                    <p className="text-[10px] text-muted-foreground">assets</p>
                  </div>
                </div>
              </div>

              <div className="mt-6 space-y-3">
                {topAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-2.5">
                      <div className="size-2.5 rounded-full bg-primary" />

                      <span className="text-xs font-medium">
                        {asset.token?.symbol || asset.token?.name}
                      </span>
                    </div>

                    <span className="text-xs font-semibold">
                      {asset?.convertedBalanceSymbol}{" "}
                      {Number(asset.convertedBalance ?? 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Security */}

            <section className="rounded-[1.5rem] border border-emerald-500/15 bg-emerald-500/[0.035] p-5 sm:p-6">
              <div className="flex items-start gap-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <ShieldCheck className="size-5" />
                </div>

                <div>
                  <p className="text-sm font-bold">Your account is protected</p>

                  <p className="mt-1 text-xs leading-5 text-muted-foreground">
                    Transactions are protected with platform security controls
                    and verification.
                  </p>
                </div>
              </div>

              <Button
                variant="outline"
                className="mt-4 w-full rounded-xl"
                onClick={() => navigate("/dashboard/settings?tab=security")}
              >
                <LockKeyhole className="mr-2 size-4" />
                Security settings
              </Button>
            </section>

            {/* P2P */}

            <section className="relative overflow-hidden rounded-[1.5rem] border border-primary/15 bg-primary/[0.045] p-5 sm:p-6">
              <Sparkles className="absolute -right-3 -top-3 size-20 text-primary/10" />

              <div className="relative">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">
                  P2P marketplace
                </p>

                <h3 className="mt-2 text-lg font-bold">
                  Find the right exchange rate.
                </h3>

                <p className="mt-1.5 text-xs leading-5 text-muted-foreground">
                  Compare verified traders and exchange currencies at
                  competitive rates.
                </p>

                <Button
                  className="mt-4 rounded-xl"
                  onClick={() => navigate("/p2p")}
                >
                  Explore P2P
                  <ArrowRight className="ml-2 size-4" />
                </Button>
              </div>
            </section>
          </div>
        </div>
      )}

      <TransactionReceipt
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        selectedTx={selectedTx}
      />
    </div>
  );
}

function QuickAction({
  icon: Icon,
  title,
  description,
  onClick,
}: {
  icon: typeof Plus;
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group rounded-xl border border-border/60 bg-background p-4 text-left transition-all hover:-translate-y-0.5 hover:border-primary/30 hover:shadow-md"
    >
      <div className="flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
        <Icon className="size-4" />
      </div>

      <p className="mt-3 text-xs font-bold">{title}</p>

      <p className="mt-0.5 text-[10px] text-muted-foreground">{description}</p>
    </button>
  );
}

function DashboardSectionHeader({
  title,
  description,
  action,
  onClick,
}: {
  title: string;
  description: string;
  action: string;
  onClick: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/50 p-5 sm:p-6">
      <div>
        <p className="text-sm font-bold">{title}</p>

        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="shrink-0 rounded-lg text-xs"
        onClick={onClick}
      >
        {action}
        <ChevronRight className="ml-1 size-3.5" />
      </Button>
    </div>
  );
}
